import React, { useMemo } from 'react';
import { Filter } from '@fabric-ui/components';
import { cn } from '@fabric-ui/utils';
import { Box, Collapse, Typography, useMediaQuery, useTheme } from '@mui/material';
import { keepPreviousData } from '@tanstack/react-query';
import { addCustomFiltersAsExpression, cleanEmptyFilters, filterToExpression } from '@utils/filterUtils';
import stringToFilter from '@utils/stringToFIlter';
import { useAvContext } from '../../../context/AvContextProvider';
import useQueryObject from '../../../hooks/useQueryObjectSql';
import DraggableWidget from '../../CustomDashboards/components/DraggableWidget';
import renderWidget from '../../CustomDashboards/components/Widgets/RenderWidget';
import {
  BarCategoryWidgetTypes,
  BarLegendPosition,
  BarSubType,
  DisplayModeOptions,
  DwQueryRequest,
  PieBasicLegendPosition,
  PieCategoryWidgetTypes,
  PieLegendStyles,
  PieSubType,
  SortDir,
  WidgetCategory,
} from '../../CustomDashboards/types';
import { filtersFieldToIgnore } from '../ticket.types';
import { IEntityViewChartsProps, ISummarizedCollapseItem } from './types';
import { EntityViewWidgetWrapper, getKeyValueData } from './utils';

const getIncidentBySeverityWidget = ({ filters, isSmallScreen }: { filters: Filter; isSmallScreen: boolean }): PieCategoryWidgetTypes => ({
  id: '',
  definition: {
    title: { title: 'Incidents by Severity' },
    coords: { x: 0, y: 0, h: 3, w: 0 },
    custom: {
      showTotals: true,
      legend: {
        position: PieBasicLegendPosition.Top,
        style: PieLegendStyles.Table,
        showValues: !isSmallScreen,
        showPercentage: false,
      },
      fieldsPalette: {},
    },
  },
  category: WidgetCategory.Pie,
  type: PieSubType.Donut,
  requests: [
    {
      filter: !!filters && Object.keys(filters).length ? filters : undefined,
      select: {
        dims: [{ name: 'incident.severity' }],
        metrics: [
          {
            name: 'total_incidents_granular',
            alias: 'total_incidents_granular',
          },
        ],
      },
      groupBy: ['incident.severity'],
      sorting: [
        {
          name: 'incident.severity',
          dir: SortDir.DESC,
        },
      ],
      top: { size: 100, groupOthers: false, offset: 0 },
      projectionId: { name: 'incidents', builtIn: true },
      distinct: false,
    },
  ],
});

const getIncidentByVerdictWidget = ({ filters, isSmallScreen }: { filters: Filter; isSmallScreen: boolean }): BarCategoryWidgetTypes => ({
  id: '',
  definition: {
    title: { title: 'Incident by Verdict' },
    coords: { x: 0, y: 0, h: 3, w: 0 },
    custom: {
      fieldsPalette: {},
      legend: {
        position: BarLegendPosition.None,
      },
      rowsColumnSwitch: false,
      displayMode: DisplayModeOptions.FIT,
      hideLabel: !isSmallScreen,
    },
  },
  category: WidgetCategory.Bar,
  type: BarSubType.StackedVertical,
  requests: [
    {
      filter: !!filters && Object.keys(filters).length ? filters : undefined,
      select: {
        dims: [{ name: 'incident.verdict' }],
        metrics: [
          {
            name: 'total_incidents_granular',
            alias: 'total_incidents_granular',
          },
        ],
      },
      groupBy: ['incident.verdict'],
      sorting: [
        {
          name: 'total_incidents_granular',
          dir: SortDir.DESC,
        },
      ],
      top: { size: 50, groupOthers: false, offset: 0 },
      projectionId: { name: 'incidents', builtIn: true },
      distinct: false,
    },
  ],
});

const getIncidentByCurrentStatusWidget = ({
  filters,
  isSmallScreen,
}: {
  filters: Filter;
  isSmallScreen: boolean;
}): BarCategoryWidgetTypes => ({
  id: '',
  definition: {
    title: { title: 'Incidents by Status' },
    coords: { x: 0, y: 0, h: 3, w: 0 },
    custom: {
      fieldsPalette: {},
      legend: {
        position: BarLegendPosition.None,
      },
      rowsColumnSwitch: false,
      displayMode: DisplayModeOptions.FIT,
      hideLabel: !isSmallScreen,
    },
  },
  category: WidgetCategory.Bar,
  type: BarSubType.StackedVertical,
  requests: [
    {
      filter: !!filters && Object.keys(filters).length ? filters : undefined,
      select: {
        dims: [{ name: 'incident.current_status.name' }],
        metrics: [
          {
            name: 'total_incidents_granular',
            alias: 'total_incidents_granular',
          },
        ],
      },
      groupBy: ['incident.current_status.name'],
      sorting: [
        {
          name: 'incident.current_status.name',
          dir: SortDir.DESC,
        },
      ],
      top: { size: 50, groupOthers: false, offset: 0 },
      projectionId: { name: 'incidents', builtIn: true },
      distinct: false,
    },
  ],
});

const IncidentsViewCharts: React.FC<IEntityViewChartsProps> = ({
  filters: f,
  isExpanded = true,
  setIsExpanded,
  extraFilters,
  searchFields,
}) => {
  const theme = useTheme();
  const {
    accountEntities: { fieldTypeMap },
  } = useAvContext();

  const isSmallScreen = useMediaQuery(theme.breakpoints.down(1501));
  const filteredFilters = Object.keys(f).reduce(
    (acc, field) =>
      (!filtersFieldToIgnore.includes(field) || field === 'search') && !!f[field].length
        ? {
            ...acc,
            [field]: f[field],
          }
        : acc,
    { search: [] }
  );
  const entityExtraFiltersObject = stringToFilter(extraFilters || '');
  const regularFilters = filterToExpression(fieldTypeMap, filteredFilters);
  const filters = addCustomFiltersAsExpression({
    expressionFiltersObj: cleanEmptyFilters({ and: { operands: [regularFilters, entityExtraFiltersObject] } }),
    operand: 'or',
    operator: 'contains',
    fields: searchFields,
    value: f?.search?.[0],
    fieldTypeMap,
  });

  const incidentBySeverityWidget = getIncidentBySeverityWidget({ filters, isSmallScreen });
  const incidentByVerdictWidget = getIncidentByVerdictWidget({ filters, isSmallScreen });
  const incidentByCurrentStatusWidget = getIncidentByCurrentStatusWidget({ filters, isSmallScreen });

  const { data: topIncidentByVerdictData, isLoading: topIncidentByVerdictLoading } = useQueryObject({
    queryObject: incidentByVerdictWidget.requests.map(request => ({ ...request, top: { ...request.top, size: 4 } })) as DwQueryRequest[],
    options: { placeholderData: keepPreviousData },
    onSuccess: data =>
      getKeyValueData({
        data,
        dataKey: incidentByVerdictWidget.requests[0].select.metrics[0].name,
        titleKey: incidentByVerdictWidget.requests[0].select.dims[0].name,
        nullValueHeader: 'No Verdict',
      }),
  });

  const { data: topIncidentByCurrentStatusData, isLoading: topIncidentByCurrentStatusLoading } = useQueryObject({
    queryObject: incidentByCurrentStatusWidget.requests.map(request => ({
      ...request,
      top: { ...request.top, size: 5 },
    })) as DwQueryRequest[],
    options: { placeholderData: keepPreviousData },
    onSuccess: data =>
      getKeyValueData({
        data,
        dataKey: incidentByCurrentStatusWidget.requests[0].select.metrics[0].name,
        titleKey: incidentByCurrentStatusWidget.requests[0].select.dims[0].name,
        nullValueHeader: 'No Status',
      }),
  });

  const summaryItems: ISummarizedCollapseItem[] = useMemo(
    () => [
      {
        title: 'Incidents by Status',
        data: topIncidentByCurrentStatusData,
        isLoading: topIncidentByCurrentStatusLoading,
        showIcon: false,
      },
      {
        title: 'Incident by Verdict',
        data: topIncidentByVerdictData,
        isLoading: topIncidentByVerdictLoading,
        showIcon: false,
      },
    ],
    [topIncidentByCurrentStatusData, topIncidentByCurrentStatusLoading, topIncidentByVerdictData, topIncidentByVerdictLoading]
  );

  return (
    <EntityViewWidgetWrapper
      isExpanded={isExpanded}
      setIsExpanded={setIsExpanded}
      summaryItemsProps={{ items: summaryItems, hideDivider: false }}>
      <Collapse in={isExpanded} timeout="auto">
        <Box className={cn('flex h-[208px] gap-3')}>
          <Box
            key={incidentByCurrentStatusWidget.definition.title.title}
            className={cn('relative flex-1 flex-shrink-0 bg-surface-base-primary')}>
            <DraggableWidget
              widget={incidentByCurrentStatusWidget}
              customToolbar={
                <Typography className="whitespace-nowrap pb-[8px]" variant="h6">
                  {incidentByCurrentStatusWidget.definition.title.title}
                </Typography>
              }>
              {renderWidget({ widget: incidentByCurrentStatusWidget })}
            </DraggableWidget>
          </Box>
          <Box
            key={incidentBySeverityWidget.definition.title.title}
            className={cn('relative flex-1 flex-shrink-0 bg-surface-base-primary')}>
            <DraggableWidget
              widget={incidentBySeverityWidget}
              customToolbar={
                <Typography className="whitespace-nowrap" variant="h6">
                  {incidentBySeverityWidget.definition.title.title}
                </Typography>
              }>
              {renderWidget({ widget: incidentBySeverityWidget })}
            </DraggableWidget>
          </Box>
          <Box key={incidentByVerdictWidget.definition.title.title} className={cn('relative flex-1 flex-shrink-0 bg-surface-base-primary')}>
            <DraggableWidget
              widget={incidentByVerdictWidget}
              customToolbar={
                <Typography className="whitespace-nowrap pb-[8px]" variant="h6">
                  {incidentByVerdictWidget.definition.title.title}
                </Typography>
              }>
              {renderWidget({ widget: incidentByVerdictWidget })}
            </DraggableWidget>
          </Box>
        </Box>
      </Collapse>
    </EntityViewWidgetWrapper>
  );
};

export default IncidentsViewCharts;
